import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import ConfirmDelete from "@/components/ConfirmDelete";
import Combobox from "@/components/Combobox";
import { Plus, Trash2, X, Layers, Pencil, Cable, Search } from "lucide-react";

// Lista gestibile e ricercabile (add + rename inline + delete), riutilizzabile.
function SearchableManageList({
  testidPrefix, icon: Icon, items, placeholder, emptyText,
  newValue, setNewValue, onAdd, editId, setEditId, onRename, onRemove,
  deleteTitle, deleteDesc,
}) {
  const [q, setQ] = useState("");
  const filtered = items.filter((t) => (t.nome || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input data-testid={`panel-new-${testidPrefix}-input`} value={newValue} onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()} placeholder={placeholder} className="h-9 bg-white max-w-xs" />
        <Button size="sm" data-testid={`panel-add-${testidPrefix}-button`} onClick={onAdd} className="bg-blue-700 hover:bg-blue-800">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi
        </Button>
      </div>
      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input data-testid={`search-${testidPrefix}`} value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca…" className="h-9 bg-white pl-8" />
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">{emptyText}</p>
      ) : (
        <div className="max-h-64 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-md">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`${testidPrefix}-item-${t.id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
              <Icon className="h-3.5 w-3.5 text-blue-700 flex-shrink-0" />
              {editId === t.id ? (
                <input autoFocus defaultValue={t.nome} data-testid={`rename-${testidPrefix}-input-${t.id}`}
                  onKeyDown={(e) => { if (e.key === "Enter") onRename(t, e.target.value); if (e.key === "Escape") setEditId(null); }}
                  onBlur={(e) => onRename(t, e.target.value)}
                  className="h-7 flex-1 px-2 bg-white border border-blue-300 rounded text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              ) : (
                <button data-testid={`rename-${testidPrefix}-${t.id}`} onClick={() => setEditId(t.id)}
                  className="flex-1 text-left text-sm text-slate-800 hover:text-blue-700 truncate" title="Clicca per rinominare">{t.nome}</button>
              )}
              <ConfirmDelete title={deleteTitle} description={deleteDesc} actionTestid={`confirm-delete-${testidPrefix}-${t.id}`} onConfirm={() => onRemove(t.id)}>
                <button data-testid={`delete-${testidPrefix}-${t.id}`} className="text-slate-400 hover:text-rose-600 flex-shrink-0" aria-label="Elimina"><Trash2 className="h-4 w-4" /></button>
              </ConfirmDelete>
            </div>
          ))}
          {filtered.length === 0 && <div className="px-3 py-4 text-sm text-slate-400">Nessun risultato per “{q}”.</div>}
        </div>
      )}
    </div>
  );
}

// Pannelli a comparsa: Contabilità (multiple per commessa), Tratte, Aree Cavo.
export default function ContabilitaManagePanels({
  coPanel, setCoPanel, contabilitaList, contabilitaId, setContabilitaId,
  coName, setCoName, createContabilita, coEditId, setCoEditId, renameContabilita,
  removeContabilita, listini, setCoListini,
  coTipo, setCoTipo, coSubId, setCoSubId, subappaltatori = [],
  trattePanel, setTrattePanel, tratte, panelNewTratta, setPanelNewTratta,
  addTrattaFromPanel, editTrattaId, setEditTrattaId, renameTratta, removeTratta,
  areaCavoPanel, setAreaCavoPanel, areeCavo = [], panelNewAreaCavo, setPanelNewAreaCavo,
  addAreaCavoFromPanel, editAreaCavoId, setEditAreaCavoId, renameAreaCavo, removeAreaCavo,
  isAdmin = false,
}) {
  const listiniForTipo = (tipo) => listini.filter((l) => l.tipo === (tipo === "subappalto" ? "subappalto" : "attivo"));
  const [pendingListino, setPendingListino] = useState(null); // {co, newId}
  const listinoName = (id) => (listini.find((l) => l.id === id) || {}).nome || "";
  const handleListinoChange = (co, v) => {
    const current = (co.listini_ids || [])[0] || "";
    const next = v || "";
    if (next === current) return;
    if (current && next) { setPendingListino({ co, newId: next }); return; }
    setCoListini(co, next ? [next] : []);
  };
  return (
    <>
      {coPanel && (
        <div data-testid="contabilita-panel" className="bg-white rounded-md border border-slate-200 shadow-sm p-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Contabilità della commessa</span>
            <button onClick={() => setCoPanel(false)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Tipo</label>
              <Select value={coTipo} onValueChange={(v) => { setCoTipo(v); if (v === "attiva") setCoSubId(""); }}>
                <SelectTrigger data-testid="panel-contabilita-tipo" className="h-9 w-40 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attiva">Attiva</SelectItem>
                  <SelectItem value="subappalto">Subappalto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {coTipo === "subappalto" && (
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Subappaltatore</label>
                <Select value={coSubId} onValueChange={setCoSubId}>
                  <SelectTrigger data-testid="panel-contabilita-subappaltatore" className="h-9 w-52 bg-white"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                  <SelectContent>
                    {subappaltatori.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Nessuna azienda (Anagrafiche)</div>}
                    {subappaltatori.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex-1 min-w-[180px]">
              <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Nome</label>
              <Input data-testid="panel-new-contabilita-input" value={coName} onChange={(e) => setCoName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createContabilita()} placeholder="Nome nuova contabilità…" className="h-9 bg-white" />
            </div>
            <Button size="sm" data-testid="panel-add-contabilita-button" onClick={createContabilita} className="h-9 bg-blue-700 hover:bg-blue-800">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi
            </Button>
          </div>
          <div className="space-y-2">
            {contabilitaList.map((co) => (
              <div key={co.id} data-testid={`contabilita-item-${co.id}`} className={`flex flex-wrap items-center gap-2 rounded-md border p-2 ${co.id === contabilitaId ? "border-blue-300 bg-blue-50/40" : "border-slate-200"}`}>
                {coEditId === co.id ? (
                  <input autoFocus defaultValue={co.nome} data-testid={`rename-contabilita-input-${co.id}`}
                    onKeyDown={(e) => { if (e.key === "Enter") renameContabilita(co, e.target.value); if (e.key === "Escape") setCoEditId(null); }}
                    onBlur={(e) => renameContabilita(co, e.target.value)}
                    className="h-8 w-48 px-2 bg-white border border-blue-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                ) : (
                  <button data-testid={`select-contabilita-${co.id}`} onClick={() => setContabilitaId(co.id)} className="text-sm font-semibold text-slate-800 hover:text-blue-700">{co.nome}</button>
                )}
                <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${co.tipo === "subappalto" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                  {co.tipo === "subappalto" ? `Subappalto${co.subappaltatore ? " · " + co.subappaltatore : ""}` : "Attiva"}
                </span>
                <button data-testid={`rename-contabilita-${co.id}`} onClick={() => setCoEditId(co.id)} className="text-slate-400 hover:text-blue-700" title="Rinomina"><Pencil className="h-3.5 w-3.5" /></button>
                <div className="flex-1 min-w-[220px]">
                  <Combobox testid={`contabilita-listino-${co.id}`} triggerClassName="w-full"
                    placeholder={`Listino ${co.tipo === "subappalto" ? "subappalto" : "attivo"}…`}
                    value={(co.listini_ids || [])[0] || ""}
                    onChange={(v) => handleListinoChange(co, v)}
                    options={listiniForTipo(co.tipo).map((l) => ({ value: l.id, label: l.nome }))} />
                </div>
                {contabilitaList.length > 1 && isAdmin && (
                  <ConfirmDelete title="Eliminare contabilità?" description="Verranno eliminate tutte le tratte e righe di questa contabilità." actionTestid={`confirm-delete-contabilita-${co.id}`} onConfirm={() => removeContabilita(co)}>
                    <button data-testid={`delete-contabilita-${co.id}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </ConfirmDelete>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {trattePanel && (
        <div data-testid="tratte-panel" className="bg-white rounded-md border border-slate-200 shadow-sm p-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Tratte della commessa</span>
            <button onClick={() => setTrattePanel(false)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
          <SearchableManageList testidPrefix="tratta" icon={Layers} items={tratte}
            placeholder="Nome nuova tratta…" emptyText="Nessuna tratta. Aggiungine una qui sopra."
            newValue={panelNewTratta} setNewValue={setPanelNewTratta} onAdd={addTrattaFromPanel}
            editId={editTrattaId} setEditId={setEditTrattaId} onRename={renameTratta} onRemove={removeTratta}
            deleteTitle="Eliminare tratta?" deleteDesc="Verranno eliminate tutte le righe di contabilità di questa tratta." />
        </div>
      )}
      {areaCavoPanel && (
        <div data-testid="aree-cavo-panel" className="bg-white rounded-md border border-slate-200 shadow-sm p-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Aree</span>
            <button onClick={() => setAreaCavoPanel(false)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
          <SearchableManageList testidPrefix="area-cavo" icon={Cable} items={areeCavo}
            placeholder="Nome nuova area…" emptyText="Nessuna area. Aggiungine una qui sopra."
            newValue={panelNewAreaCavo} setNewValue={setPanelNewAreaCavo} onAdd={addAreaCavoFromPanel}
            editId={editAreaCavoId} setEditId={setEditAreaCavoId} onRename={renameAreaCavo} onRemove={removeAreaCavo}
            deleteTitle="Eliminare area?" deleteDesc="L'area verrà rimossa dall'elenco. Le righe che la usano manterranno il testo." />
        </div>
      )}
      <AlertDialog open={!!pendingListino} onOpenChange={(o) => { if (!o) setPendingListino(null); }}>
        <AlertDialogContent data-testid="change-listino-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Cambiare il listino associato?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingListino && (
                <>La contabilità <strong>“{pendingListino.co.nome}”</strong> è già associata al listino <strong>“{listinoName((pendingListino.co.listini_ids || [])[0])}”</strong>. Vuoi sostituirlo con <strong>“{listinoName(pendingListino.newId)}”</strong>? Le voci disponibili cambieranno di conseguenza.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="change-listino-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="change-listino-confirm-btn" onClick={() => { if (pendingListino) setCoListini(pendingListino.co, [pendingListino.newId]); setPendingListino(null); }}>
              Sostituisci
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
